import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Send,
  Loader2,
  FileCode,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  Plus,
  ArrowLeft,
  RefreshCw,
  Sparkles,
  Check,
  X,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { Button } from '../components/Button';

interface FileItem {
  id: string;
  path: string;
  file_type: string;
  updated_at: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: Array<{
    tool: string;
    args: Record<string, unknown>;
    result: string;
  }>;
  plan?: string[];
}

interface Website {
  id: string;
  name: string;
  slug: string;
}

export function WebsiteBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  useAuth(); // Ensure user is authenticated
  const { request } = useApi();

  const [website, setWebsite] = useState<Website | null>(null);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['src']));
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [previewKey, setPreviewKey] = useState(0); // Force iframe refresh

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Load website data
  useEffect(() => {
    if (id) {
      loadWebsite();
    }
  }, [id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadWebsite = async () => {
    setIsLoading(true);
    try {
      const data = await request<{ website: Website; files: FileItem[] }>(`/api/websites/${id}`);
      setWebsite(data.website);
      setFiles(data.files);

      // Create or get conversation
      const convData = await request<{ conversation: { id: string } }>(
        `/api/websites/${id}/conversations`,
        { method: 'POST' }
      );
      setConversationId(convData.conversation.id);

      // Load existing messages
      const msgData = await request<{ messages: Message[] }>(
        `/api/websites/${id}/conversations/${convData.conversation.id}/messages`
      );
      setMessages(msgData.messages || []);
    } catch (error) {
      console.error('Error loading website:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isSending) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage.trim(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsSending(true);

    try {
      const response = await request<{
        response: string;
        toolCalls: Message['toolCalls'];
        plan: string[];
        files: FileItem[];
      }>(`/api/websites/${id}/chat`, {
        method: 'POST',
        body: {
          message: userMessage.content,
          conversationId,
        },
      });

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.response,
        toolCalls: response.toolCalls,
        plan: response.plan,
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setFiles(response.files);
      // Refresh preview after file changes
      setPreviewKey((k) => k + 1);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: `Error: ${error instanceof Error ? error.message : 'Failed to get response'}`,
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsSending(false);
    }
  };

  const loadFile = async (path: string) => {
    try {
      const data = await request<{ file: { content: string } }>(
        `/api/websites/${id}/file?path=${encodeURIComponent(path)}`
      );
      setSelectedFile(path);
      setFileContent(data.file.content);
    } catch (error) {
      console.error('Error loading file:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleFolder = (folder: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folder)) {
        next.delete(folder);
      } else {
        next.add(folder);
      }
      return next;
    });
  };

  // Build file tree structure
  const buildFileTree = (files: FileItem[]) => {
    const tree: Record<string, { files: FileItem[]; folders: Set<string> }> = { '': { files: [], folders: new Set() } };

    files.forEach((file) => {
      const parts = file.path.split('/');
      let currentPath = '';

      parts.forEach((part, index) => {
        if (index === parts.length - 1) {
          // It's a file
          if (!tree[currentPath]) {
            tree[currentPath] = { files: [], folders: new Set() };
          }
          tree[currentPath].files.push(file);
        } else {
          // It's a folder
          if (!tree[currentPath]) {
            tree[currentPath] = { files: [], folders: new Set() };
          }
          tree[currentPath].folders.add(part);
          currentPath = currentPath ? `${currentPath}/${part}` : part;
        }
      });
    });

    return tree;
  };

  const renderFileTree = (path: string = '', depth: number = 0): React.ReactNode[] => {
    const tree = buildFileTree(files);
    const node = tree[path];
    if (!node) return [];

    const items: React.ReactNode[] = [];

    // Render folders
    Array.from(node.folders)
      .sort()
      .forEach((folder) => {
        const fullPath = path ? `${path}/${folder}` : folder;
        const isExpanded = expandedFolders.has(fullPath);

        items.push(
          <div key={fullPath}>
            <button
              onClick={() => toggleFolder(fullPath)}
              className="w-full flex items-center gap-1 px-2 py-1 hover:bg-white/5 rounded text-left text-sm"
              style={{ paddingLeft: `${depth * 12 + 8}px` }}
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-white/40" />
              ) : (
                <ChevronRight className="w-4 h-4 text-white/40" />
              )}
              <FolderOpen className="w-4 h-4 text-yellow-400" />
              <span className="text-white/80">{folder}</span>
            </button>
            {isExpanded && renderFileTree(fullPath, depth + 1)}
          </div>
        );
      });

    // Render files
    node.files
      .sort((a, b) => a.path.localeCompare(b.path))
      .forEach((file) => {
        const fileName = file.path.split('/').pop();
        items.push(
          <button
            key={file.path}
            onClick={() => loadFile(file.path)}
            className={`w-full flex items-center gap-2 px-2 py-1 hover:bg-white/5 rounded text-left text-sm ${
              selectedFile === file.path ? 'bg-primary-500/20 text-primary-400' : ''
            }`}
            style={{ paddingLeft: `${depth * 12 + 28}px` }}
          >
            <FileCode className="w-4 h-4 text-blue-400" />
            <span className="text-white/80 truncate">{fileName}</span>
          </button>
        );
      });

    return items;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-900 flex flex-col">
      {/* Header */}
      <header className="h-14 border-b border-white/10 flex items-center justify-between px-4 shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-white/5 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5 text-white/60" />
          </button>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary-400" />
            <span className="font-semibold text-white">{website?.name || 'Website Builder'}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={loadWebsite}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Chat Panel */}
        <div className="w-96 border-r border-white/10 flex flex-col">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="text-center py-12">
                <Sparkles className="w-12 h-12 text-primary-500/50 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">Start Building</h3>
                <p className="text-white/60 text-sm">
                  Tell me what you want to create and I'll build it for you.
                </p>
              </div>
            )}

            {messages.map((message) => (
              <div
                key={message.id}
                className={`${
                  message.role === 'user' ? 'ml-8' : 'mr-8'
                }`}
              >
                <div
                  className={`rounded-xl p-3 ${
                    message.role === 'user'
                      ? 'bg-primary-500 text-white'
                      : 'bg-white/5 text-white/90'
                  }`}
                >
                  {message.plan && (
                    <div className="mb-3 p-2 bg-black/20 rounded-lg">
                      <p className="text-xs text-white/60 mb-2">Plan:</p>
                      {message.plan.map((step, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <Check className="w-3 h-3 text-green-400" />
                          <span>{step}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  {message.toolCalls && message.toolCalls.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-white/10">
                      <p className="text-xs text-white/40 mb-1">Actions taken:</p>
                      {message.toolCalls.map((tc, i) => (
                        <div key={i} className="text-xs text-white/60">
                          • {tc.tool}: {tc.args.path as string || JSON.stringify(tc.args)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isSending && (
              <div className="mr-8">
                <div className="bg-white/5 rounded-xl p-3 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-primary-400" />
                  <span className="text-sm text-white/60">Thinking...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-white/10">
            <div className="relative">
              <textarea
                ref={inputRef}
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Describe what you want to build..."
                className="w-full px-4 py-3 pr-12 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/40 resize-none focus:outline-none focus:border-primary-500"
                rows={3}
              />
              <button
                onClick={sendMessage}
                disabled={!inputMessage.trim() || isSending}
                className="absolute right-3 bottom-3 p-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Preview Panel */}
        <div className="flex-1 bg-white flex flex-col">
          <div className="h-10 bg-slate-800 border-b border-white/10 flex items-center justify-between px-4">
            <span className="text-xs text-white/60">Preview</span>
            <button
              onClick={() => setPreviewKey((k) => k + 1)}
              className="p-1 hover:bg-white/10 rounded"
              title="Refresh preview"
            >
              <RefreshCw className="w-3 h-3 text-white/40" />
            </button>
          </div>
          <div className="flex-1 relative bg-white">
            {id ? (
              <iframe
                key={previewKey}
                src={`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}/preview/${id}?v=${previewKey}`}
                className="absolute inset-0 w-full h-full border-0"
                title="Website Preview"
                sandbox="allow-scripts allow-same-origin"
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
                <p className="text-slate-500">Select a website to preview</p>
              </div>
            )}
          </div>
        </div>

        {/* File Tree Panel */}
        <div className="w-64 border-l border-white/10 flex flex-col">
          <div className="h-10 border-b border-white/10 flex items-center justify-between px-4">
            <span className="text-xs text-white/60 uppercase tracking-wider">Files</span>
            <button className="p-1 hover:bg-white/5 rounded">
              <Plus className="w-4 h-4 text-white/40" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto py-2">
            {renderFileTree()}
          </div>

          {/* Selected file preview */}
          {selectedFile && (
            <div className="border-t border-white/10 max-h-64 overflow-hidden flex flex-col">
              <div className="h-8 bg-white/5 flex items-center justify-between px-3 shrink-0">
                <span className="text-xs text-white/60 truncate">{selectedFile}</span>
                <button onClick={() => setSelectedFile(null)}>
                  <X className="w-3 h-3 text-white/40" />
                </button>
              </div>
              <pre className="flex-1 overflow-auto p-3 text-xs text-white/70 font-mono">
                {fileContent}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
